// client/src/pages/ShopPOS/hooks/usePOSOffline.ts
import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { indexedDBService, PendingOperation } from "@/lib/db/indexed-db"; // CORRECCIÓN: Importar interfaz actualizada
import { toast } from "@/hooks/use-toast";
import { openCashierShift } from "@/lib/api";
import { useOfflineContext } from "@/lib/contexts/offline-context";
import { AuthService } from "@/lib/services/auth-offline";
import { syncManager } from "@/lib/services/sync-manager";

interface UsePOSOfflineProps {
  currentShop: any;
  currentShift: any;
  cart: any[];
  displayCartItems: any[];
  cartTotal: number;
  cartTotalWithSurcharge: number;
  inventoryData: any;
  realUserId?: string;
}

export function usePOSOffline({
  currentShop,
  currentShift,
  cart,
  displayCartItems,
  cartTotal,
  cartTotalWithSurcharge,
  inventoryData,
  realUserId,
}: UsePOSOfflineProps) {
  const queryClient = useQueryClient();

  // Obtener conectividad del contexto global
  const { isOfflineMode, isServerReachable, checkServer } = useOfflineContext();

  // Estados de sincronización
  const [pendingSalesCount, setPendingSalesCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showOfflineBadge, setShowOfflineBadge] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{
    current: number;
    total: number;
    isSyncing: boolean;
  }>({ current: 0, total: 0, isSyncing: false });

  // {NUEVO SECCIÓN: Estado para turno pendiente de autorización}
  // Esta sección mantiene el estado del turno que requiere validación de supervisor
  const [pendingAuthShift, setPendingAuthShift] = useState<any>(null);

  // Ref para persistir estado offline entre renders
  const wasOfflineRef = useRef(isOfflineMode);

  // Ref para evitar doble sincronización
  const isSyncingRef = useRef(false);

  // HELPER: Obtener shopId de forma segura (sesión > prop > fallback)
  const getShopId = async (): Promise<string | null> => {
    // 1. Intentar obtener de sesión autenticada (fuente de verdad)
    const session = await AuthService.getSession();
    if (session?.shopId) {
      return session.shopId;
    }
    // 2. Fallback a prop si está disponible
    if (currentShop?.id) {
      return currentShop.id;
    }
    // 3. No hay shopId disponible
    return null;
  };
  // COMENTARIO: Este helper centraliza la lógica de obtención de shopId, priorizando la sesión validada

  // Verificar servidor periódicamente y sincronizar automáticamente
  useEffect(() => {
    const checkAndSync = async () => {
      const isReachable = await checkServer();

      console.log(
        `🔍 [AUTO-SYNC] Estado: wasOffline=${wasOfflineRef.current}, isReachable=${isReachable}, isOfflineMode=${isOfflineMode}`,
      );

      if (wasOfflineRef.current && isReachable) {
        console.log(
          "🔄 [AUTO-SYNC] Conexión recuperada - Iniciando sincronización automática",
        );

        // CORRECCIÓN: Usar helper seguro (sesión > prop) para obtener shopId válido
        const shopId = (await getShopId()) || currentShop?.id;
        if (shopId) {
          const pendingOps =
            await indexedDBService.getPendingOperationsByShop(shopId);
          const pendingSales = await indexedDBService.getUnsyncedSales(shopId);

          if (pendingOps.length > 0 || pendingSales.length > 0) {
            console.log(
              `📊 [AUTO-SYNC] Encontradas ${pendingOps.length} operaciones y ${pendingSales.length} ventas pendientes`,
            );

            // {Verificar si hay turno offline pendiente de autorización local}
            const pendingAuth = await indexedDBService.getPendingAuthorizationOfflineShift(shopId);
            if (pendingAuth) {
              console.log(
                "🔐 [AUTO-SYNC] Turno offline pendiente de autorización detectado, mostrando modal",
              );
              setPendingAuthShift({
                shiftId: pendingAuth.id,
                countedCash: pendingAuth.countedCash,
                expectedCash: pendingAuth.expectedCash,
                difference: pendingAuth.difference,
                denominations: pendingAuth.denominationBreakdown || {},
                notes: pendingAuth.notes || "",
                requiresAuthorization: true,
                shopId,
                isLocal: true,
              });
              localStorage.setItem(
                "pendingAuthShift",
                JSON.stringify({
                  shiftId: pendingAuth.id,
                  countedCash: pendingAuth.countedCash,
                  expectedCash: pendingAuth.expectedCash,
                  difference: pendingAuth.difference,
                  denominations: pendingAuth.denominationBreakdown || {},
                  notes: pendingAuth.notes || "",
                }),
              );
              wasOfflineRef.current = !isReachable;
              return;
            }

            // Reducido de 2000ms a 500ms para evitar que el reload interrumpa el sync
            setTimeout(() => {
              syncPendingSales();
            }, 500);
          } else {
            console.log("📭 [AUTO-SYNC] No hay operaciones pendientes");
          }
        }
      }

      wasOfflineRef.current = !isReachable;
    };

    checkAndSync();
    const interval = setInterval(checkAndSync, 10000);
    return () => clearInterval(interval);
  }, [checkServer, currentShop?.id, isOfflineMode]);

  // {ESCUCHAR EVENTOS DE SINCRONIZACIÓN PARA ACTUALIZAR BADGE}
  useEffect(() => {
    const unsubscribe = syncManager.on(async (event) => {
      if (event.type === "sync:start") {
        setIsSyncing(true);
        setSyncProgress({ current: 0, total: 0, isSyncing: true });
      } else if (event.type === "sync:progress") {
        setSyncProgress({
          current: event.data?.current || 0,
          total: event.data?.total || 0,
          isSyncing: true,
        });
      } else if (event.type === "sync:pending-auth") {
        const data = event.data;
        if (data?.shiftId) {
          console.log("🔐 [OFFLINE] Turno pendiente de autorización detectado por sync-manager");
          setPendingAuthShift({
            shiftId: data.shiftId,
            countedCash: data.countedCash,
            expectedCash: data.expectedCash,
            difference: data.difference,
            denominations: data.denominations || {},
            notes: data.notes || "",
            requiresAuthorization: true,
            shopId: data.shopId,
            isLocal: true,
          });
          localStorage.setItem(
            "pendingAuthShift",
            JSON.stringify({
              shiftId: data.shiftId,
              countedCash: data.countedCash,
              expectedCash: data.expectedCash,
              difference: data.difference,
              denominations: data.denominations || {},
              notes: data.notes || "",
            }),
          );
        }
      } else if (event.type === "sync:complete" || event.type === "sync:offline") {
        setSyncProgress({ current: 0, total: 0, isSyncing: false });
        setIsSyncing(false);
        await checkPendingSales();
      }
    });
    return unsubscribe;
  }, [currentShop?.id]);

  // Verificar ventas pendientes
  const checkPendingSales = async () => {
    // CORRECCIÓN: Usar getShopId() para obtener shopId de sesión o fallback
    const shopId = (await getShopId()) || currentShop?.id;
    if (!shopId) return;

    try {
      const unsyncedSales = await indexedDBService.getUnsyncedSales(shopId);
      const pendingOps = await indexedDBService.getPendingOperationsByShop(shopId);
      // Contar todas las operaciones pendientes (ventas + turnos + otras)
      const totalPending = unsyncedSales.length + pendingOps.filter(op => op.status === "pending").length;
      setPendingSalesCount(totalPending);
      if (totalPending > 0) {
        setShowOfflineBadge(true);
      }
      console.log(`📊 [PENDING-CHECK] Ventas sin sync: ${unsyncedSales.length}, Ops pendientes: ${pendingOps.filter(op => op.status === "pending").length}, Total: ${totalPending}`);
    } catch (error) {
      console.error("Error verificando ventas pendientes:", error);
    }
  };
  // COMENTARIO: Ahora checkPendingSales funciona incluso si currentShop no está disponible pero hay sesión válida.

  // Cargar ventas pendientes al iniciar
  useEffect(() => {
    if (currentShop?.id) {
      checkPendingSales();
      cleanupOrphanedShifts();
      checkPendingAuthorizationShift(); // {NUEVO: Verificar turnos pendientes al iniciar}
    }
  }, [currentShop?.id]);

  // Limpiar turnos huérfanos
  const cleanupOrphanedShifts = async () => {
    try {
      // CORRECCIÓN: Usar helper seguro (sesión > prop)
      const shopId = (await getShopId()) || currentShop?.id;
      if (!shopId) return;
      console.log("🧹 [CLEANUP] Verificando turnos huérfanos...");

      const tx = indexedDBService["db"]?.transaction(
        "offline_shifts",
        "readonly",
      );
      if (!tx) return;

      const store = tx.objectStore("offline_shifts");
      const index = store.index("shopId");
      const shifts = await new Promise<any[]>((resolve, reject) => {
        const request = index.getAll(shopId);
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });

      console.log(
        `📊 [CLEANUP] Encontrados ${shifts.length} turnos en IndexedDB`,
      );

      const now = new Date();
      let cleanedCount = 0;

      for (const shift of shifts) {
        let shouldDelete = false;
        let reason = "";

        if (shift.status === "closed") {
          if (shift.synced === 1) {
            shouldDelete = true;
            reason = "sincronizado y cerrado";
          } else {
            const pendingOps = await indexedDBService.getPendingOperations();
            const hasPendingClose = pendingOps.some(
              (op: any) =>
                op.entityType === "shift" && op.data?.shiftId === shift.id,
            );

            if (!hasPendingClose) {
              shouldDelete = true;
              reason = "cerrado sin operación pendiente (huérfano)";
            }
          }
        }

        if (shift.status === "open") {
          const openedAt = new Date(shift.openedAt);
          const hoursSinceOpen =
            (now.getTime() - openedAt.getTime()) / (1000 * 60 * 60);
          if (hoursSinceOpen > 24) {
            shouldDelete = true;
            reason = `abierto hace ${hoursSinceOpen.toFixed(1)} horas (huérfano)`;
          }
        }

        if (shift.isBackup === true) {
          const backedUpAt = new Date(shift.backedUpAt || shift.createdAt);
          const hoursSinceBackup =
            (now.getTime() - backedUpAt.getTime()) / (1000 * 60 * 60);
          if (hoursSinceBackup > 12) {
            shouldDelete = true;
            reason = `backup de hace ${hoursSinceBackup.toFixed(1)} horas`;
          }
        }

        if (shouldDelete) {
          await indexedDBService.deleteOfflineShift(shift.id);
          cleanedCount++;
          console.log(`🗑️ [CLEANUP] Turno eliminado: ${shift.id} (${reason})`);
        }
      }

      console.log(`✅ [CLEANUP] Limpiados ${cleanedCount} turnos huérfanos`);
    } catch (error) {
      console.error("❌ [CLEANUP] Error limpiando turnos:", error);
    }
  };

  // {NUEVA FUNCIÓN: Verificar si hay turnos pendientes de autorización en el backend}
  // Esta sección consulta al backend si existe algún turno en estado pending_authorization
  const checkPendingAuthorizationShift = async () => {
    if (!currentShop?.id) return;

    try {
      console.log(
        "🔍 [PENDING-CHECK] Verificando turnos pendientes de autorización...",
      );

      const response = await fetch(
        `/api/shops/${currentShop.id}/pending-authorization-shift`,
      );

      if (response.ok) {
        const data = await response.json();

        if (data.shift) {
          console.log(
            "🔐 [PENDING-CHECK] Turno pendiente encontrado:",
            data.shift.id,
          );

          const pendingShiftData = {
            shiftId: data.shift.id,
            countedCash: data.shift.countedCash,
            expectedCash: data.shift.expectedCash,
            difference: data.shift.difference,
            denominations: data.shift.denominationBreakdown || {},
            notes: data.shift.notes,
            requiresAuthorization: true,
            shopId: currentShop.id,
          };

          // {Actualizar estado local}
          setPendingAuthShift(pendingShiftData);

          // {Guardar en localStorage}
          localStorage.setItem(
            "pendingAuthShift",
            JSON.stringify(pendingShiftData),
          );
        }
      }
    } catch (error) {
      console.error(
        "❌ [PENDING-CHECK] Error verificando turnos pendientes:",
        error,
      );
    }
  };

  // Abrir turno offline
  const openOfflineShift = async (
    cashRegisterId: string,
    initialCash: string,
  ) => {
    if (!currentShop) throw new Error("No hay tienda");

    // Usar el employeeId real almacenado, o el último conocido
    const storedEmployeeId = localStorage.getItem("lastEmployeeId");
    const rawEmployeeId = storedEmployeeId || realUserId || "offline_user";
    // Si el employeeId es shop_xxx (sintético), convertirlo a virtual_owner_ que el server acepta
    const employeeId = rawEmployeeId.startsWith("shop_")
      ? `virtual_owner_${currentShop.id}`
      : rawEmployeeId;
    console.log("📴 [OFFLINE] Usando employeeId:", employeeId);

    const offlineShift = {
      id: `offline_shift_${Date.now()}`,
      shopId: currentShop.id,
      cashRegisterId,
      employeeId: employeeId,
      initialCash,
      openedBy: "Usuario Offline",
      openedAt: new Date().toISOString(),
      status: "open",
      cashSales: "0.00",
      cardSales: "0.00",
      transferSales: "0.00",
      totalSales: "0.00",
      expectedCash: initialCash,
      synced: false,
      isOffline: true,
    };

    await indexedDBService.saveOfflineShift(offlineShift);

    // Encolar creación del turno para sincronizar cuando vuelva la conexión
    const effectiveShopId = await getShopId();
    if (effectiveShopId) {
      await syncManager.enqueue({
        type: "create",
        entityType: "shift",
        data: {
          shiftId: offlineShift.id,
          shopId: effectiveShopId,
          cashRegisterId,
          employeeId,
          initialCash,
          openedBy: "Usuario Offline",
          openedAt: offlineShift.openedAt,
        },
        shopId: effectiveShopId,
        priority: "high",
      });
      console.log("📤 [OFFLINE] Apertura de turno encolada en syncManager");
    }

    console.log("🎯 [OFFLINE-SHIFT] ========================================");
    console.log("🎯 [OFFLINE-SHIFT] ID DEL TURNO OFFLINE CREADO:");
    console.log("🎯 [OFFLINE-SHIFT]", offlineShift.id);
    console.log("🎯 [OFFLINE-SHIFT] Monto inicial:", initialCash);
    console.log("🎯 [OFFLINE-SHIFT] EmployeeId:", employeeId);
    console.log("🎯 [OFFLINE-SHIFT] ========================================");

    return offlineShift;
  };

  // Cerrar turno offline
  const closeOfflineShift = async (
    shiftId: string,
    countedCash: string,
    notes: string,
    denominations: Record<string, number>,
    closeType: string,
    expectedCash: string,
  ) => {
    console.log("📴 [OFFLINE] ========================================");
    console.log("📴 [OFFLINE] INICIANDO CIERRE DE TURNO OFFLINE");
    console.log("📴 [OFFLINE] shiftId:", shiftId);
    console.log("📴 [OFFLINE] countedCash:", countedCash);
    console.log("📴 [OFFLINE] expectedCash:", expectedCash);
    console.log("📴 [OFFLINE] closeType:", closeType);
    console.log("📴 [OFFLINE] ========================================");

    // {NUEVO: Calcular expectedCash correcto si no se proporcionó o es 0}
    // {Esta sección asegura que expectedCash = initialCash + cashSales del turno offline}
    let finalExpectedCash = expectedCash;

    if (!finalExpectedCash || parseFloat(finalExpectedCash) === 0) {
      // Obtener el turno offline de IndexedDB para calcular el esperado
      const offlineShift = await indexedDBService.getOfflineShiftById(shiftId);
      if (offlineShift) {
        const initialCash = parseFloat(offlineShift.initialCash) || 0;

        // Calcular ventas en efectivo del turno (desde IndexedDB)
        const offlineSales = await indexedDBService.getSales(
          offlineShift.shopId,
        );

        // {CORRECCIÓN: sale.total ya es number, no necesita parseFloat}
        const cashSalesInShift = offlineSales
          .filter(
            (sale) =>
              sale.shiftId === shiftId && sale.paymentMethod === "efectivo",
          )
          .reduce((sum: number, sale) => sum + sale.total, 0); // ← Tipado explícito + sin parseFloat

        finalExpectedCash = (initialCash + cashSalesInShift).toFixed(2);
        console.log(
          `🧮 [OFFLINE] expectedCash recalculado: ${initialCash} + ${cashSalesInShift} = ${finalExpectedCash}`,
        );
      }
    }

    const countedCashNum = parseFloat(countedCash);
    const expectedCashNum = parseFloat(finalExpectedCash);
    const difference = countedCashNum - expectedCashNum;
    const hasDiscrepancy = Math.abs(difference) > 0.01;

    console.log(
      `📊 [OFFLINE] Diferencia: ${difference} (${hasDiscrepancy ? "DISCREPANCIA" : "EXACTO"})`,
    );

    const status = hasDiscrepancy ? "pending_authorization" : "closed";
    const discrepancyType =
      difference < 0 ? "faltante" : difference > 0 ? "sobrante" : "exacto";

    // CORRECCIÓN: Obtener shopId seguro ANTES de crear la operación
    const effectiveShopId = await getShopId();
    if (!effectiveShopId) {
      console.warn(
        "⚠️ No hay shopId válido para encolar operación de cierre de turno",
      );
      throw new Error("No se puede cerrar turno: sesión no válida");
    }

    if (hasDiscrepancy) {
      // {NO encolar shift/update - esperar código de supervisor}
      await indexedDBService.markShiftAsPendingAuthorization(
        shiftId,
        countedCash,
        finalExpectedCash,
        difference.toString(),
        denominations,
        notes,
      );
      console.log(
        "⚠️ [OFFLINE] Turno marcado como PENDIENTE DE AUTORIZACIÓN localmente (esperando código de supervisor)",
      );
    } else {
      // {Encolar cierre para auto-sync normal}
      await syncManager.enqueue({
        type: "update",
        entityType: "shift",
        data: {
          shiftId,
          countedCash,
          notes,
          denominationBreakdown: denominations,
          closeType,
          closedAt: new Date().toISOString(),
          expectedCash: finalExpectedCash,
          difference: difference.toString(),
          discrepancyType,
          status: "closed",
          requiresAuthorization: false,
        },
        shopId: effectiveShopId,
        priority: "high",
      });
      console.log("✅ [OFFLINE] Cierre de turno encolado en syncManager");
      const shiftToUpdate = await indexedDBService.getOfflineShiftById(shiftId);
      if (shiftToUpdate) {
        shiftToUpdate.closePending = true;
        shiftToUpdate.countedCash = countedCash;
        shiftToUpdate.closedAt = new Date().toISOString();
        await indexedDBService.saveOfflineShift(shiftToUpdate);
      }
      console.log("✅ [OFFLINE] Turno marcado para cierre pendiente de sincronización");
    }

    console.log("📴 [OFFLINE] CIERRE DE TURNO OFFLINE COMPLETADO");
    return { success: true, hasDiscrepancy };
  };

  // Procesar venta offline
  const processOfflineSale = async (
    method: "efectivo" | "transferencia" | "mixto",
    cashReceivedAmount: number,
    cartItems: any[],
    customer: any,
    transferData: any,
    transferAmount?: number,
  ) => {
    const allInventory = inventoryData?.data || [];

    const saleItems = cart.map((cartItem) => {
      const invItem = allInventory.find(
        (i: any) => i.id === cartItem.inventoryId,
      );
      const basePrice = invItem ? parseFloat(invItem.price) : 0;
      const surchargePercentage = cartItem.surchargePercentage || 0;
      const finalPrice = basePrice + basePrice * (surchargePercentage / 100);

      return {
        productId: invItem?.productId,
        quantity: cartItem.quantity,
        price: finalPrice,
        inventoryId: cartItem.inventoryId,
        surchargePercentage,
      };
    });

    const saleData: any = {
      id: `temp_sale_${Date.now()}`,
      shopId: currentShop!.id,
      total: method === "transferencia" ? cartTotalWithSurcharge : cartTotal,
      paymentMethod: method,
      items: saleItems,
      createdAt: new Date().toISOString(),
    };

    if (currentShift) saleData.shiftId = currentShift.id;
    if (customer) saleData.customerId = customer.id;

    if (method === "efectivo") {
      saleData.cashReceived = cashReceivedAmount;
      saleData.changeGiven = cashReceivedAmount - cartTotal;
    }

    if (method === "transferencia") {
      saleData.fullName = transferData.fullName?.trim();
      saleData.ci = transferData.ci?.trim();
      saleData.phone = transferData.phone?.trim();
      saleData.transferCode = transferData.transferCode?.trim();
      saleData.bank = transferData.bank;
      if (transferData.firstFourDigits?.trim()) {
        saleData.firstFourDigits = transferData.firstFourDigits.trim();
      }
      if (transferData.lastFourDigits?.trim()) {
        saleData.lastFourDigits = transferData.lastFourDigits.trim();
      }
    }

    // {esta sección maneja el método de pago mixto para ventas offline - CORRECCIÓN FINAL}
    if (method === "mixto") {
      // Convertir a números y validar
      const cash = Number(cashReceivedAmount);
      const transfer = Number(transferAmount);

      // Solo proceder si ambos montos son válidos y positivos
      if (cash > 0 && transfer > 0 && !isNaN(cash) && !isNaN(transfer)) {
        // ✅ Guardar explícitamente como propiedades del objeto saleData
        saleData.cashAmount = cash;
        saleData.transferAmount = transfer;
        saleData.cashReceived = cash; // Para compatibilidad con reportes de caja
        saleData.changeGiven = cash - cartTotal;

        // Datos opcionales de transferencia (solo si existen)
        if (transferData) {
          if (transferData.fullName?.trim())
            saleData.fullName = transferData.fullName.trim();
          if (transferData.ci?.trim()) saleData.ci = transferData.ci.trim();
          if (transferData.phone?.trim())
            saleData.phone = transferData.phone.trim();
          if (transferData.transferCode?.trim())
            saleData.transferCode = transferData.transferCode.trim();
          if (transferData.bank) saleData.bank = transferData.bank;
          if (transferData.destinationCard?.trim())
            saleData.destinationCard = transferData.destinationCard.trim();
          if (transferData.firstFourDigits?.trim())
            saleData.firstFourDigits = transferData.firstFourDigits.trim();
          if (transferData.lastFourDigits?.trim())
            saleData.lastFourDigits = transferData.lastFourDigits.trim();
        }

        // {LOG DE CONFIRMACIÓN: Verificar que se guardó correctamente en IndexedDB}
        console.log("💾 [OFFLINE] Venta mixto guardada con éxito:", {
          id: saleData.id,
          cashAmount: saleData.cashAmount,
          transferAmount: saleData.transferAmount,
          tipo: typeof saleData.cashAmount,
          existe: "cashAmount" in saleData && "transferAmount" in saleData,
        });
      } else {
        console.error("❌ [OFFLINE] Montos inválidos para pago mixto:", {
          cashReceivedAmount,
          transferAmount,
          cash,
          transfer,
          cartTotal,
        });
      }
    }

    await indexedDBService.saveSale({ ...saleData, synced: false });

    // {Decrementar stock localmente en IndexedDB para mantener consistencia visual}
    try {
      for (const item of cart) {
        const invId = item.inventoryId;
        const qty = item.quantity || 1;
        if (invId) {
          const invItem = await indexedDBService.getInventoryItem(invId);
          if (invItem) {
            const currentStock = Number(invItem.stock) || 0;
            const newStock = Math.max(0, currentStock - qty);
            console.log(`📦 [OFFLINE] Stock ${invId}: ${currentStock} → ${newStock}`);
            await indexedDBService.updateInventoryItem(invId, { stock: newStock }, currentShop?.id);
          } else {
            console.warn(`⚠️ [OFFLINE] Item NO encontrado en IndexedDB: ${invId}`);
          }
        }
      }
    } catch (stockError) {
      console.warn("⚠️ [OFFLINE] No se pudo decrementar stock local:", stockError);
    }

    // Refrescar UI con el stock actualizado — patch directo del cache de React Query
    if (currentShop?.id) {
      const queryCache = queryClient.getQueryCache();
      const inventoryQueries = queryCache.findAll({ queryKey: ["shopInventoryWithCart", currentShop.id] });
      const soldMap = new Map<string, number>();
      for (const c of cart) {
        const q = c.quantity || 1;
        soldMap.set(c.inventoryId, (soldMap.get(c.inventoryId) || 0) + q);
      }
      inventoryQueries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, (old: any) => {
          if (!old?.filtered?.data) return old;
          return {
            ...old,
            filtered: {
              ...old.filtered,
              data: old.filtered.data.map((item: any) => {
                const qtySold = soldMap.get(item.id);
                return qtySold
                  ? { ...item, stock: Math.max(0, Number(item.stock) - qtySold) }
                  : item;
              }),
            },
          };
        });
      });
    }

    // {Usar syncManager para encolar la operación (centralizado)}
    const effectiveShopId = await getShopId();
    if (!effectiveShopId) {
      console.warn("⚠️ No hay shopId válido para encolar operación de venta");
      throw new Error("No se puede procesar venta: sesión no válida");
    }

    await syncManager.enqueue({
      type: "create",
      entityType: "sale",
      data: saleData,
      shopId: effectiveShopId,
      priority: "high",
    });

    // Actualizar turno offline en IndexedDB (cashSales, totalSales, expectedCash)
    if (currentShift?.id && (method === "efectivo" || method === "mixto")) {
      try {
        const offlineShift = await indexedDBService.getOfflineShiftById(
          currentShift.id,
        );
        if (offlineShift) {
          const cashToAdd =
            method === "mixto" ? cashReceivedAmount || 0 : cartTotal;
          const transferToAdd = method === "mixto" ? transferAmount || 0 : 0;

          const currentCashSales = parseFloat(offlineShift.cashSales || "0");
          const currentTransferSales = parseFloat(
            offlineShift.transferSales || "0",
          );
          const currentTotalSales = parseFloat(offlineShift.totalSales || "0");
          const initialCash = parseFloat(offlineShift.initialCash || "0");

          offlineShift.cashSales = (currentCashSales + cashToAdd).toFixed(2);
          if (transferToAdd > 0) {
            offlineShift.transferSales = (
              currentTransferSales + transferToAdd
            ).toFixed(2);
          }
          offlineShift.totalSales = (currentTotalSales + cartTotal).toFixed(2);
          offlineShift.expectedCash = (
            initialCash + parseFloat(offlineShift.cashSales)
          ).toFixed(2);

          await indexedDBService.saveOfflineShift(offlineShift);
          console.log("🔄 [OFFLINE] Turno actualizado en IndexedDB:", {
            cashSales: offlineShift.cashSales,
            expectedCash: offlineShift.expectedCash,
          });
        }
      } catch (e) {
        console.warn("⚠️ [OFFLINE] No se pudo actualizar turno:", e);
      }
    }

    setPendingSalesCount((prev) => prev + 1);
    setShowOfflineBadge(true);

    return saleData;
  };

  // Sincronizar ventas pendientes usando syncManager centralizado
  const syncPendingSales = async () => {
    if (!currentShop || isSyncing || isSyncingRef.current) {
      console.log("⚠️ [SYNC] Ya hay una sincronización en curso, omitiendo");
      return;
    }

    isSyncingRef.current = true;
    setIsSyncing(true);
    setSyncProgress({ current: 0, total: 0, isSyncing: true });

    try {
      console.log("🔄 [SYNC] ========== INICIANDO SINCRONIZACIÓN ==========");

      // {Validar sesión antes de sincronizar}
      const session = await AuthService.getSession();
      if (!session?.token) {
        console.warn("⚠️ [SYNC] No hay sesión válida para sincronizar");
        toast({
          title: "Sesión expirada",
          description: "Conéctate a internet e inicia sesión para sincronizar",
          variant: "destructive",
        });
        return;
      }

      // {syncManager.syncAll() procesará las operaciones en orden:
      //  1. shift/create → crea turno en backend → guarda realId
      //  2. sale/create → usa shiftId mapeado
      //  3. shift/update → cierra turno con ID real}

      // {Usar syncManager para sincronizar TODO}
      const result = await syncManager.syncAll(session.token);
      console.log("📊 [SYNC] Resultado syncManager:", result);

      // {Actualizar UI según resultado}
      if (result.status === "completed" || result.status === "partial") {
        if (result.status === "completed") {
          toast({
            title: "✅ Sincronización completada",
            description: `${result.syncedCount} operaciones sincronizadas`,
          });
        } else {
          toast({
            title: "⚠️ Sincronización parcial",
            description: `${result.syncedCount} ok, ${result.failedCount} fallos`,
            variant: "destructive",
          });
        }

        // {Actualizar contadores y refrescar datos solo si realmente sincronizó}
        await checkPendingSales();
        queryClient.invalidateQueries({
          queryKey: ["shopSales", currentShop.id],
        });
        queryClient.invalidateQueries({
          queryKey: ["shopInventoryWithCart", currentShop.id],
        });
        queryClient.invalidateQueries({ queryKey: ["currentShift"] });
        queryClient.refetchQueries({ queryKey: ["currentShift"] });

        console.log("✅ [SYNC] ========== SINCRONIZACIÓN COMPLETADA ==========");
      } else if (result.status === "skipped") {
        console.log(
          "ℹ️ [SYNC] Sincronización ya en progreso, omitiendo toast de error",
        );
      } else {
        toast({
          title: "❌ Error de sincronización",
          description: "No se pudieron sincronizar los datos",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("❌ [SYNC] Error general:", error);
      toast({
        title: "Error de sincronización",
        description: "No se pudieron sincronizar los datos",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
      isSyncingRef.current = false;
      setSyncProgress({ current: 0, total: 0, isSyncing: false });
    }
  };

  // {NUEVA FUNCIÓN: Limpiar estado de turno pendiente}
  const clearPendingAuthShift = () => {
    console.log("🧹 [OFFLINE] Limpiando estado de turno pendiente");
    setPendingAuthShift(null);
    localStorage.removeItem("pendingAuthShift");
  };

  // {AUTORIZAR CIERRE LOCAL: Encola shift/update con código de supervisor y sincroniza}
  const authorizeLocalClose = async (authCode: string) => {
    const shopId = (await getShopId()) || currentShop?.id;
    if (!shopId) throw new Error("No hay tienda");
    if (!authCode) throw new Error("Código de autorización requerido");

    const pendingShift = await indexedDBService.getPendingAuthorizationOfflineShift(shopId);
    if (!pendingShift) throw new Error("No hay turno pendiente de autorización local");

    const countedCash = pendingShift.countedCash || "";
    const expectedCash = pendingShift.expectedCash || "0";
    const difference = pendingShift.difference || "0";

    const discrepancyType =
      parseFloat(difference) < 0
        ? "faltante"
        : parseFloat(difference) > 0
          ? "sobrante"
          : "exacto";

    // {Encolar operación de cierre con código de autorización}
    await syncManager.enqueue({
      type: "update",
      entityType: "shift",
      data: {
        shiftId: pendingShift.id,
        countedCash,
        notes: pendingShift.notes || "",
        denominationBreakdown: pendingShift.denominationBreakdown || {},
        closeType: "day_close",
        closedAt: pendingShift.closedAt || new Date().toISOString(),
        expectedCash,
        difference,
        discrepancyType,
        status: "pending_authorization",
        requiresAuthorization: true,
        authorizationCode: authCode,
      },
      shopId,
      priority: "high",
    });

    console.log("✅ [OFFLINE] Cierre con código encolado, sincronizando...");

    // {Ejecutar sync para procesar el cierre}
    const session = await AuthService.getSession();
    if (!session?.token) throw new Error("Sesión expirada");

    const result = await syncManager.syncAll(session.token);

    // {Limpiar estado local}
    clearPendingAuthShift();
    await indexedDBService.deleteOfflineShift(pendingShift.id);

    // {Refrescar UI}
    await checkPendingSales();
    if (shopId) {
      queryClient.invalidateQueries({ queryKey: ["shopSales", shopId] });
      queryClient.invalidateQueries({ queryKey: ["shopInventory", shopId] });
      queryClient.invalidateQueries({ queryKey: ["currentShift"] });
      queryClient.removeQueries({ queryKey: ["currentShift"] });
      await new Promise((resolve) => setTimeout(resolve, 500));
      await queryClient.invalidateQueries({ queryKey: ["currentShift"] });
    }

    return result;
  };

  return {
    isOfflineMode,
    isServerReachable,
    pendingSalesCount,
    isSyncing,
    showOfflineBadge,
    syncProgress,
    pendingAuthShift, // {NUEVO: Estado del turno pendiente}
    checkServer,
    checkPendingSales,
    processOfflineSale,
    syncPendingSales,
    openOfflineShift,
    closeOfflineShift,
    setShowOfflineBadge,
    setSyncProgress,
    clearPendingAuthShift, // {NUEVO: Función para limpiar}
    authorizeLocalClose, // {NUEVO: Autorizar cierre local con código de supervisor}
  };
}
